// Story 1.29 — TanStack Query hook for GET /api/v1/admin/metrics.
//
// Single endpoint, single envelope. The response carries 9 panels;
// each is either Wired (has `data`) or Unavailable (has
// `unavailable_reason` + `populates_in_story`). The SPA narrows on
// `"data" in panel` to branch render arms.
//
// PRD semantics: "refreshes on page-load" — TanStack Query's default
// `refetchOnWindowFocus: true` is the right shape. No polling.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type MonthlyCount = {
  month: string; // "YYYY-MM"
  count: number;
};

export type SignupFunnelMonth = {
  month: string;
  registered: number;
  email_verified: number;
  first_dossier_purchased: number | null;
};

export type UsersPanelData = {
  mau_by_month: MonthlyCount[];
  signup_funnel: SignupFunnelMonth[];
};

/**
 * Discriminated union: a panel is EITHER wired (has `data`) OR
 * unavailable (has `unavailable_reason` + `populates_in_story`).
 * Narrow via `"data" in panel` / `"unavailable_reason" in panel`.
 */
export type PanelEnvelope<T> =
  | { data: T }
  | { unavailable_reason: string; populates_in_story: string };

export type WindowDescriptor = {
  months: number;
  start: string;
  end: string;
};

export type TopCreatorEarningsRow = {
  creator_user_id: string;
  handle: string | null;
  display_name: string | null;
  total_eur_cents: number;
  primary_eur_cents: number;
  resale_eur_cents: number;
};

export type CreatorEarningsPanelData = {
  cumulative_eur_cents: number;
  primary_eur_cents: number;
  resale_eur_cents: number;
  top_creators: TopCreatorEarningsRow[];
};

// Story 6.7 — reconciliation panel. Red while `open_drift` is true.
export type ReconciliationRunSummary = {
  run_id: string;
  run_at: string;
  status: string; // "passed" | "drift_detected" | "failed"
  drift_cents: number;
  in_flight_count: number;
  in_flight_cents: number;
  unmatched_external_count: number;
  wallets_checked: number;
  wallets_failed: number;
};

export type ReconciliationWalletDrift = {
  wallet_ref: string;
  wallet_kind: string;
  owner_user_id: string | null;
  drift_cents: number;
  status: string; // "drift" | "failed"
  detail: string | null;
};

export type ReconciliationPanelData = {
  latest_run: ReconciliationRunSummary | null;
  open_drift: boolean;
  consecutive_clean_runs: number;
  max_settlement_latency_ms: number;
  interval_secs: number;
  drifting_wallets: ReconciliationWalletDrift[];
};

// Drops panel (wired 2026-08-14) — dossiers sold per month + top drops.
export type DropsMonth = {
  month: string;
  dossiers_sold: number;
  gmv_eur_cents: number;
};

export type TopDropRow = {
  drop_id: string;
  portfolio_title: string;
  kind: string;
  status: string;
  sold_count: number;
  gmv_eur_cents: number;
  opened_count: number;
};

export type DropsPanelData = {
  sold_by_month: DropsMonth[];
  cumulative_sold: number;
  cumulative_gmv_eur_cents: number;
  top_drops: TopDropRow[];
};

// Marketplace panel (wired 2026-08-14) — trades + GMV, sealed vs share.
export type MarketplaceMonth = {
  month: string;
  share_trades: number;
  share_gmv_eur_cents: number;
  sealed_trades: number;
  sealed_gmv_eur_cents: number;
};

export type MarketplaceTotals = {
  share_trades: number;
  share_gmv_eur_cents: number;
  sealed_trades: number;
  sealed_gmv_eur_cents: number;
};

export type MarketplacePanelData = {
  trades_by_month: MarketplaceMonth[];
  cumulative: MarketplaceTotals;
};

// Tier telemetry panel (wired 2026-08-14) — configured vs actual per tier.
export type TierTelemetryRow = {
  tier: string;
  configured_probability: number;
  revealed_count: number;
  revealed_fraction: number;
  // How many of this tier's revealed shares are signed. Rendered as a dimmed
  // "(x)" next to the count, shown only when > 0.
  signed_count: number;
  // Configured signature odds for this tier (server-side single source:
  // reveal_orchestrator::signed_odds). Shown as an unobtrusive secondary line.
  configured_signed_probability: number;
};

export type TierTelemetryPortfolio = {
  portfolio_id: string;
  portfolio_title: string;
  total_revealed: number;
  tiers: TierTelemetryRow[];
};

export type TierTelemetryPanelData = {
  portfolios: TierTelemetryPortfolio[];
  note: string;
};

// Founder's-multiple panel (wired 2026-08-14).
export type FoundersMultiplePanelData = {
  secondary_sales_count: number;
  secondary_gmv_eur_cents: number;
  avg_secondary_price_cents: number;
  avg_primary_price_cents: number;
  multiple: number | null;
};

// VAT-exposure panel (Story 11.2, wired 2026-09-01; turned into EUROS by
// Story 11.9, 2026-09-02 — SAME panel key, extended in place).
//
// A top-up from a country nobody has researched is ACCEPTED and charged 0%
// (owner ruling R8, 2026-09-01: no geo-restriction). What is shown here is the
// LIABILITY that decision creates, in euros — not a payment count. The owner's
// own backdating answer is that a nil-threshold jurisdiction can claim VAT on
// ALL purchases ever taken from a country, retroactively and with no time
// limit, so the exposure grows with cumulative sales value and never expires.
//
// ⛔ Nothing here blocks a payment. It is a monitor and a trigger for research.
export type UnresearchedCountryRow = {
  country: string; // ISO 3166-1 alpha-2
  /** ⚑ Cumulative gross taken from this country under the unresearched treatment. */
  gross_eur_cents: number;
  topup_count: number;
  first_seen_at: string;
  last_seen_at: string;
  /** The 25%-of-gross reserve still held, off the `vat_provision` ledger account. */
  provision_held_eur_cents: number;
  provision_released_eur_cents: number;
  /**
   * The rate the worst case is priced at, when one is on record. `null` means
   * the exposure is genuinely **unquantified** — an honest "we do not know what
   * this one costs", which the AC requires as an output rather than a blank cell.
   */
  indicative_rate_bps: number | null;
  worst_case_exposure_eur_cents: number | null;
  /**
   * The register's status for this country TODAY, when it has one. Stamps are
   * immutable, so a country researched after its top-ups were taken keeps its
   * historical `unresearched_country` rows forever — this is how the row says
   * "resolved since".
   */
  register_status: string | null;
  research_artifact_path: string;
};

/** The researched side: what we owe, where. From the ledger, never a placeholder. */
export type ResearchedPositionRow = {
  country: string;
  status: string | null;
  /** `eu_oss` / `domestic` / `own_rail` (the UK) / `special` (the US). */
  reporting_rail: string | null;
  rate_bps: number | null;
  vat_payable_eur_cents: number;
  /**
   * ⚑ The reserve still held against this country. An `own_rail` jurisdiction
   * (the UK) is hedged on EVERY top-up at its own published rate, because its
   * duty runs whether or not Greenhold is registered in Denmark — so a
   * researched country can hold a reserve and the surface has to say how much.
   */
  provision_held_eur_cents: number;
  provision_released_eur_cents: number;
  /**
   * ⚑ Researched rate AT OR ABOVE the provision rate — historical
   * under-provision, or (at exactly equal) a reserve with no headroom left.
   * `≥`, not `>`: Norway's 25% ties the setting exactly.
   */
  at_or_above_provision_rate: boolean;
  research_artifact: string | null;
};

export type UnresearchedCountriesPanelData = {
  countries: UnresearchedCountryRow[];
  researched: ResearchedPositionRow[];
  total_topups: number;
  country_count: number;
  total_gross_eur_cents: number;
  total_provision_held_eur_cents: number;
  total_provision_released_eur_cents: number;
  /** Sum of the QUANTIFIED worst cases only — never a zero standing in for a gap. */
  total_worst_case_exposure_eur_cents: number;
  unquantified_country_count: number;
  provision_rate_bps: number;
  total_vat_payable_eur_cents: number;
};

// VAT registration panel (Story 11.3, wired 2026-09-01).
//
// The registration state is DATA with an effective-from date, changed only
// through the audited admin action beside this panel — never a deploy and never
// an environment variable. The two counters are independent because either can
// end the unregistered phase first: a platform whose buyers are mostly Danish
// trips DKK 50,000, one whose buyers are mostly German trips EUR 10,000.
//
// ⚠ Amounts are in each counter's OWN currency — the Danish one in øre, the EU
// one in EUR cents. `gross_eur_cents` is the pre-conversion figure, carried so
// the conversion is auditable from the panel alone.
export type VatThresholdCounter = {
  kind: "danish_domestic" | "eu_cross_border";
  label: string;
  currency: "DKK" | "EUR";
  turnover_cents: number;
  threshold_cents: number;
  headroom_cents: number;
  proportion_bps: number;
  alarm_at_bps: number;
  alarm_at_cents: number;
  alarm_reached: boolean;
  tripped: boolean;
  gross_eur_cents: number;
};

export type VatRegistrationPanelData = {
  current: {
    state: "not_registered" | "registered";
    effective_from: string | null;
    vat_registration_number: string | null;
    eu_destination_vat_from: string | null;
    recorded_at: string | null;
    recorded_by: string | null;
    note: string | null;
    /** `false` when no state has ever been recorded — absence IS not_registered. */
    ever_recorded: boolean;
  };
  counters: {
    year: number;
    /** Provisional (ERM II central rate). A setting, never a literal. */
    eur_to_dkk_rate_bps: number;
    danish_domestic: VatThresholdCounter;
    eu_cross_border: VatThresholdCounter;
    unattributed_gross_eur_cents: number;
  };
};

export type MetricsDashboardEnvelope = {
  generated_at: string;
  window: WindowDescriptor;
  users: PanelEnvelope<UsersPanelData>;
  drops: PanelEnvelope<DropsPanelData>;
  marketplace: PanelEnvelope<MarketplacePanelData>;
  creator_earnings: PanelEnvelope<CreatorEarningsPanelData>;
  reconciliation: PanelEnvelope<ReconciliationPanelData>;
  tier_telemetry: PanelEnvelope<TierTelemetryPanelData>;
  founders_multiple: PanelEnvelope<FoundersMultiplePanelData>;
  unresearched_countries: PanelEnvelope<UnresearchedCountriesPanelData>;
  vat_registration: PanelEnvelope<VatRegistrationPanelData>;
};

export type GetAdminMetricsResponse = {
  data: MetricsDashboardEnvelope;
};

async function fetchAdminMetrics(): Promise<GetAdminMetricsResponse> {
  const response = await fetch("/api/v1/admin/metrics", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin metrics fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as GetAdminMetricsResponse;
}

export function useAdminMetrics() {
  return useQuery<GetAdminMetricsResponse, Error>({
    queryKey: ["admin-metrics"],
    queryFn: fetchAdminMetrics,
    staleTime: 60_000, // 1 min — same-page tab switches feel instant
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true, // PRD "refreshes on page-load" semantics
    refetchInterval: false, // EXPLICIT — no auto-polling per PRD
  });
}
